import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { generateQRCode, generateCarQRCode, getQRCodes, getRestaurant, updateQRCode, deleteQRCode } from '../services/api';
import { toast } from '../lib/toast';
import AppShell from '../components/layout/AppShell';
import './QRCodes.css';

const QRCodes = () => {
  const { t, i18n } = useTranslation();
  const { user, restaurants, isAdmin, isManager, isVerified } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantSlug, setRestaurantSlug] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQrId, setEditingQrId] = useState(null);
  const [editTableNumber, setEditTableNumber] = useState('');
  const [savingQrEdit, setSavingQrEdit] = useState(false);
  const [deleteQrConfirmOpen, setDeleteQrConfirmOpen] = useState(false);
  const [deleteQrConfirmQr, setDeleteQrConfirmQr] = useState(null);
  const [generatingCarQR, setGeneratingCarQR] = useState(false);
  const [regenerateCarConfirmOpen, setRegenerateCarConfirmOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const paramId = searchParams.get('restaurant');
    if (isManager()) {
      if (!isVerified()) return;
      if (restaurants.length === 0) return;
      const managerRestaurantId = restaurants[0].id;
      if (paramId && parseInt(paramId, 10) !== managerRestaurantId) {
        navigate(`/admin/qr-codes?restaurant=${managerRestaurantId}`, { replace: true });
        return;
      }
      setRestaurantId(managerRestaurantId);
    } else if (isAdmin()) {
      if (paramId) {
        setRestaurantId(parseInt(paramId, 10));
      } else if (restaurants.length > 0) {
        // Prefer non-demo restaurant (id !== 1) as default
        const nonDemoRestaurant = restaurants.find(r => r.id !== 1);
        const defaultRestaurant = nonDemoRestaurant || restaurants[0];
        setRestaurantId(defaultRestaurant.id);
      }
    }
  }, [user, restaurants, searchParams, isAdmin, isManager, isVerified, navigate]);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    setQrCodes([]);
    setEditingQrId(null);
    setEditTableNumber('');
    fetchRestaurantInfo();
    fetchQRCodes();
  }, [restaurantId]);

  const fetchRestaurantInfo = async () => {
    if (!restaurantId) return;
    try {
      const response = await getRestaurant(restaurantId);
      setRestaurantName(response.data.name || '');
      setRestaurantSlug(response.data.slug || '');
    } catch (error) {
      console.error('Error fetching restaurant info:', error);
    }
  };

  const fetchQRCodes = async () => {
    if (!restaurantId) return;
    try {
      setLoading(true);
      const response = await getQRCodes(restaurantId);
      setQrCodes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const escapeHtml = (str) => String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const openQRPrintWindow = (qr, forPdf = false) => {
    const imgSrc = qr.qr_code_url || qr.qr_code;
    if (!imgSrc) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const isCarQR = qr.qr_type === 'car';
    const title = isCarQR
      ? t('qrCodes.carOrderQR')
      : t('qrCodes.table', { number: qr.table_number });
    const subtitle = isCarQR
      ? t('qrCodes.carQRScanSubtitle')
      : t('qrCodes.scanToOpen');
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    const lang = escapeHtml(i18n.language || 'en');
    const safeTitle = escapeHtml(title);
    const safeSubtitle = escapeHtml(subtitle);
    const safeImgSrc = escapeHtml(imgSrc);
    const safeAlt = escapeHtml(t('qrCodes.qrCodeAlt'));
    const pdfHint = forPdf
      ? `<p style="font-size:0.85rem;color:#888;margin-top:0.5rem;">${escapeHtml(t('qrCodes.pdfHint'))}</p>`
      : '';
    win.document.write(`
      <!DOCTYPE html>
      <html dir="${dir}" lang="${lang}">
        <head><meta charset="utf-8" /><title>${safeTitle}</title></head>
        <body style="margin:0;padding:2rem;display:flex;flex-direction:column;align-items:center;font-family:sans-serif;">
          <h2 style="margin-bottom:1rem;">${safeTitle}</h2>
          <p style="color:#666;margin-bottom:1rem;">${safeSubtitle}</p>
          <img src="${safeImgSrc}" alt="${safeAlt}" style="width:280px;height:280px;" />
          ${pdfHint}
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 250);
  };

  const handlePrintQR = (qr) => openQRPrintWindow(qr, false);
  const handleDownloadPDF = (qr) => openQRPrintWindow(qr, true);

  const handleEditQR = (qr) => {
    const id = qr.id ?? qr.ID ?? qr.qr_code_id;
    if (id == null) return;
    setEditingQrId(id);
    setEditTableNumber(qr.table_number ?? qr.tableNumber ?? '');
  };

  const handleSaveQREdit = async () => {
    const id = editingQrId != null ? Number(editingQrId) : null;
    if (id == null || id === 0 || !editTableNumber.trim()) return;
    try {
      setSavingQrEdit(true);
      await updateQRCode(restaurantId, id, { table_number: editTableNumber.trim() });
      await fetchQRCodes();
      setEditingQrId(null);
      setEditTableNumber('');
      toast.success(t('qrCodes.successUpdated'));
    } catch (error) {
      console.error('Error updating QR code:', error);
      if (error.response?.status === 404) {
        await fetchQRCodes();
        setEditingQrId(null);
        setEditTableNumber('');
        toast.error(t('qrCodes.errors.notFound'));
        return;
      }
      const msg = error.response?.data?.error
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || t('qrCodes.errors.updateFailed');
      toast.error(msg);
    } finally {
      setSavingQrEdit(false);
    }
  };

  const handleCancelQREdit = () => {
    setEditingQrId(null);
    setEditTableNumber('');
  };

  const handleDeleteQRClick = (qr) => {
    setDeleteQrConfirmQr(qr);
    setDeleteQrConfirmOpen(true);
  };

  const handleDeleteQRConfirm = async () => {
    if (!deleteQrConfirmQr) return;
    const qrId = deleteQrConfirmQr.id ?? deleteQrConfirmQr.ID ?? deleteQrConfirmQr.qr_code_id;
    setDeleteQrConfirmOpen(false);
    setDeleteQrConfirmQr(null);
    if (qrId == null) return;
    try {
      await deleteQRCode(restaurantId, qrId);
      await fetchQRCodes();
      toast.success(t('qrCodes.successDeleted'));
    } catch (error) {
      console.error('Error deleting QR code:', error);
      toast.error(error.response?.data?.error || t('qrCodes.errors.deleteFailed'));
    }
  };

  const handleGenerateQR = async (e) => {
    e.preventDefault();
    if (!tableNumber.trim()) {
      toast.error(t('validation.required'));
      return;
    }
    try {
      await generateQRCode(restaurantId, tableNumber.trim());
      await fetchQRCodes();
      setTableNumber('');
      toast.success(t('qrCodes.successGenerated'));
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error(error.response?.data?.error || t('qrCodes.errors.generateFailed'));
    }
  };

  const handleGenerateCarQR = async () => {
    if (!restaurantId) return;
    try {
      setGeneratingCarQR(true);
      await generateCarQRCode(restaurantId);
      await fetchQRCodes();
      toast.success(t('qrCodes.successGenerated'));
    } catch (error) {
      console.error('Error generating car QR code:', error);
      toast.error(error.response?.data?.error || t('qrCodes.errors.generateFailed'));
    } finally {
      setGeneratingCarQR(false);
    }
  };

  const handleRegenerateCarQRConfirm = async () => {
    setRegenerateCarConfirmOpen(false);
    await handleGenerateCarQR();
  };

  const carQr = qrCodes.find(q => q.qr_type === 'car') || null;
  const tableQrCodes = qrCodes.filter(q => q.qr_type !== 'car');

  if (!user) return null;

  if (isManager() && !isVerified()) {
    return (
      <div className="qr-codes-page">
        <div className="qr-codes-panel">
          <div className="pending-verification-container" style={{ padding: '2rem', textAlign: 'center' }}>
            <h2>{t('qrCodes.pendingVerification.title')}</h2>
            <p>{t('qrCodes.pendingVerification.message')}</p>
            <Link to="/manager/dashboard" className="btn btn-primary">{t('qrCodes.backToDashboard')}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (isManager() && restaurants.length === 0) {
    return (
      <div className="qr-codes-page">
        <div className="qr-codes-panel">
          <p>{t('qrCodes.noRestaurant.managerMessage')}</p>
          <Link to="/manager/dashboard" className="btn btn-primary">{t('qrCodes.backToDashboard')}</Link>
        </div>
      </div>
    );
  }

  if (restaurantId == null) {
    if (isAdmin() && restaurants.length === 0) {
      return (
        <div className="qr-codes-page">
          <div className="qr-codes-panel">
            <p>{t('qrCodes.noRestaurant.adminMessage')}</p>
            <Link to="/admin/dashboard" className="btn btn-primary">{t('orders.noRestaurant.goToDashboard')}</Link>
          </div>
        </div>
      );
    }
    return (
      <div className="qr-codes-page">
        <div className="qr-codes-panel">
          <div className="loading-container"><div className="loading"></div></div>
        </div>
      </div>
    );
  }

  return (
    <AppShell maxWidth={false}>
      <div className="qr-codes-panel">
        <div className="qr-codes-header">
          <div>
            <h1>{t('qrCodes.title')}</h1>
            <p className="qr-codes-subtitle">{restaurantName}</p>
          </div>
          <div className="qr-codes-actions">
            {isAdmin() && restaurants.length > 1 && (
              <select
                className="form-input qr-codes-restaurant-select"
                value={restaurantId}
                onChange={(e) => {
                  const id = parseInt(e.target.value, 10);
                  setRestaurantId(id);
                  navigate(`/admin/qr-codes?restaurant=${id}`, { replace: true });
                }}
              >
                {restaurants.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
            <Link to={isManager() ? '/manager/dashboard' : '/admin/dashboard'} className="btn btn-secondary">
              {t('qrCodes.backToDashboard')}
            </Link>
            <Link to={`/admin?restaurant=${restaurantId}`} className="btn btn-primary">
              {t('qrCodes.manageMenu')}
            </Link>
          </div>
        </div>

        {carQr ? (
          <section className="qr-car-section" aria-labelledby="qr-car-section-title">
            <div className="qr-car-info">
              <div className="qr-car-badge" aria-hidden="true">🚗</div>
              <div>
                <h2 id="qr-car-section-title" className="qr-car-title">{t('qrCodes.carOrderQR')}</h2>
                <p className="qr-car-description">{t('qrCodes.carQRInstructions')}</p>
              </div>
            </div>
            <div className="qr-car-media">
              <div className="qr-car-image-wrap">
                <img
                  src={carQr.qr_code_url || carQr.qr_code}
                  alt={t('qrCodes.carOrderQR')}
                  className="qr-car-image"
                />
              </div>
              <div className="qr-car-actions">
                <button
                  type="button"
                  className="qr-card-icon-btn"
                  onClick={() => handlePrintQR(carQr)}
                  title={t('qrCodes.printQR')}
                  aria-label={t('qrCodes.printQR')}
                >
                  <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="qr-card-icon-btn"
                  onClick={() => handleDownloadPDF(carQr)}
                  title={t('qrCodes.downloadPDF')}
                  aria-label={t('qrCodes.downloadPDF')}
                >
                  <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="qr-card-icon-btn qr-card-delete-btn"
                  onClick={() => handleDeleteQRClick(carQr)}
                  title={t('qrCodes.deleteQR')}
                  aria-label={t('qrCodes.deleteQR')}
                >
                  <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
                <a
                  href={`${window.location.origin}/menu/${restaurantSlug || restaurantId}?type=car`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-small qr-car-action-btn"
                >
                  {t('qrCodes.previewMenu')}
                </a>
                <button
                  type="button"
                  className="btn btn-secondary btn-small qr-car-action-btn"
                  onClick={() => setRegenerateCarConfirmOpen(true)}
                  disabled={generatingCarQR}
                >
                  {t('qrCodes.regenerateCarQR')}
                </button>
              </div>
            </div>
          </section>
        ) : (
          <section className="qr-car-section qr-car-section--empty" aria-labelledby="qr-car-empty-title">
            <div className="qr-car-info">
              <div className="qr-car-badge" aria-hidden="true">🚗</div>
              <div>
                <h2 id="qr-car-empty-title" className="qr-car-title">{t('qrCodes.carQRNotGeneratedTitle')}</h2>
                <p className="qr-car-description">{t('qrCodes.carQRNotGeneratedDescription')}</p>
              </div>
            </div>
            <div className="qr-car-actions qr-car-actions--standalone">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGenerateCarQR}
                disabled={generatingCarQR || loading}
              >
                {t('qrCodes.generateCarQR')}
              </button>
            </div>
          </section>
        )}

        <h2 className="qr-codes-section-title">{t('qrCodes.sectionTitle')}</h2>
        <form onSubmit={handleGenerateQR} className="qr-form">
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            <label>{t('qrCodes.tableNumber')}</label>
            <input
              type="text"
              className="form-input"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder={t('qrCodes.tableNumberPlaceholder')}
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-end' }}>
            {t('qrCodes.generateButton')}
          </button>
        </form>

        <p className="qr-instructions" style={{ marginBottom: '1rem' }}>
          {t('qrCodes.instructions')}
        </p>

        {loading ? (
          <div className="loading-container"><div className="loading"></div></div>
        ) : tableQrCodes.length === 0 ? (
          <div className="qr-cards-empty">
            <div className="qr-cards-empty-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v1" />
              </svg>
            </div>
            <h3 className="qr-cards-empty-title">{t('qrCodes.emptyTitle')}</h3>
            <p className="qr-cards-empty-description">{t('qrCodes.emptyDescription')}</p>
          </div>
        ) : (
          <div className="qr-cards-grid">
            {tableQrCodes.map((qr) => {
              const qrId = qr.id ?? qr.ID ?? qr.qr_code_id;
              return (
                <div key={qrId != null ? qrId : qr.table_number} className="qr-card">
                  <div className="qr-card-header">
                    {editingQrId === qrId ? (
                      <div className="qr-card-edit-row">
                        <input
                          type="text"
                          className="form-input qr-card-edit-input"
                          value={editTableNumber}
                          onChange={(e) => setEditTableNumber(e.target.value)}
                          placeholder={t('qrCodes.tableNumber')}
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveQREdit()}
                        />
                        <div className="qr-card-edit-actions">
                          <button
                            type="button"
                            className="qr-card-icon-btn qr-card-save-btn"
                            onClick={handleSaveQREdit}
                            disabled={savingQrEdit || !editTableNumber.trim()}
                            title={t('common.save')}
                            aria-label={t('common.save')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="qr-card-icon-btn"
                            onClick={handleCancelQREdit}
                            disabled={savingQrEdit}
                            title={t('common.cancel')}
                            aria-label={t('common.cancel')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="qr-card-table">{t('qrCodes.table', { number: qr.table_number })}</span>
                        <div className="qr-card-actions">
                          <button
                            type="button"
                            className="qr-card-icon-btn"
                            onClick={() => handleEditQR(qr)}
                            title={t('qrCodes.editTableName')}
                            aria-label={t('qrCodes.editTableName')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="qr-card-icon-btn"
                            onClick={() => handlePrintQR(qr)}
                            title={t('qrCodes.printQR')}
                            aria-label={t('qrCodes.printQR')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 6 2 18 2 18 9" />
                              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="qr-card-icon-btn"
                            onClick={() => handleDownloadPDF(qr)}
                            title={t('qrCodes.downloadPDF')}
                            aria-label={t('qrCodes.downloadPDF')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="qr-card-icon-btn qr-card-delete-btn"
                            onClick={() => handleDeleteQRClick(qr)}
                            title={t('qrCodes.deleteQR')}
                            aria-label={t('qrCodes.deleteQR')}
                          >
                            <svg className="qr-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="qr-card-image-wrap">
                    <img
                      src={qr.qr_code_url || qr.qr_code}
                      alt={t('qrCodes.table', { number: qr.table_number })}
                      className="qr-card-image"
                    />
                  </div>
                  <a
                    href={`${window.location.origin}/menu/${restaurantSlug || restaurantId}?table=${encodeURIComponent(qr.table_number)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary btn-small qr-card-preview"
                  >
                    {t('qrCodes.previewMenu')}
                  </a>
                  <p className="qr-card-hint">{t('qrCodes.printHint')}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={deleteQrConfirmOpen} onClose={() => { setDeleteQrConfirmOpen(false); setDeleteQrConfirmQr(null); }}>
        <DialogTitle>{t('qrCodes.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          {deleteQrConfirmQr && (
            deleteQrConfirmQr.qr_type === 'car'
              ? t('qrCodes.deleteConfirm.carMessage')
              : t('qrCodes.deleteConfirm.message', { number: deleteQrConfirmQr.table_number })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteQrConfirmOpen(false); setDeleteQrConfirmQr(null); }}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteQRConfirm} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={regenerateCarConfirmOpen} onClose={() => setRegenerateCarConfirmOpen(false)}>
        <DialogTitle>{t('qrCodes.regenerateCarConfirm.title')}</DialogTitle>
        <DialogContent>
          {t('qrCodes.regenerateCarConfirm.message')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateCarConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleRegenerateCarQRConfirm} color="primary" variant="contained" disabled={generatingCarQR}>
            {t('qrCodes.regenerateCarConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

export default QRCodes;
